import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./HomePage.css";
import { authFetch } from "../api";
import { useLanguage } from "../LanguageContext";

function HomePage() {
    const [quizzes, setQuizzes] = useState([]);
    const [allQuizzes, setAllQuizzes] = useState([]);
    const [quizTypes, setQuizTypes] = useState([]);
    const [selectedType, setSelectedType] = useState("All");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(8);
    const [loading, setLoading] = useState(false);

    const { lang } = useLanguage();  // 🔹 get current language

    const API_BASE = (
        process.env.REACT_APP_API_URL ||
        process.env.REACT_APP_API_BASE_URL ||
        "http://localhost:8000/api/"
    ).replace(/\/$/, "");

    // Optional: Django base for back button
    const DJANGO_BASE =
        process.env.REACT_APP_DJANGO_PUBLIC_URL || "https://www.funlishland.com";

    const getPaginatedData = (data) => {
        const start = (currentPage - 1) * pageSize;
        return data.slice(start, start + pageSize);
    };

    const getTotalPages = (data) => Math.ceil(data.length / pageSize);

    const fetchQuizzes = async () => {
        setLoading(true);
        let allResults = [];
        let nextUrl = `${ API_BASE }/quizzes/`;

        try {
            while (nextUrl) {
                const res = await authFetch(nextUrl);
                if (!res.ok) throw new Error(`HTTP ${ res.status }`);
                const data = await res.json();

                const results = data.results || data; // supports paginated & non-paginated
                allResults = [...allResults, ...results];

                if (data.next) {
                    let url = data.next;

                    if (!url.startsWith("http")) {
                        url = `${ API_BASE }${ url }`;
                    }

                    url = url.replace(/^http:\/\//, "https://");
                    nextUrl = url;
                } else {
                    nextUrl = null;
                }
            }

            setAllQuizzes(allResults);
            setQuizzes(allResults);
            setQuizTypes(["All", ...new Set(allResults.map((q) => q.quiz_type))]);
        } catch (err) {
            console.error("Failed to load quizzes:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuizzes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const filtered =
            selectedType === "All"
                ? allQuizzes
                : allQuizzes.filter((quiz) => quiz.quiz_type === selectedType);
        setQuizzes(filtered);
        setCurrentPage(1);
    }, [selectedType, allQuizzes]);

    const paginatedQuizzes = getPaginatedData(quizzes);
    const totalPages = getTotalPages(quizzes);

    const handlePrev = () => currentPage > 1 && setCurrentPage((p) => p - 1);
    const handleNext = () =>
        currentPage < totalPages && setCurrentPage((p) => p + 1);

    const formatType = (type) =>
        type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    // 🔹 Texts depending on language
    const titleText =
        lang === "sr"
            ? "🎮 Dobrodošli u FunlishLand kvizove"
            : lang === "de"
                ? "🎮 Willkommen bei den FunlishLand-Quizzen"
                : "🎮 Welcome to FunlishLand Quizzes";

    const subtitleText =
        lang === "sr"
            ? "Izaberite igru ili kviz i počnite sa učenjem!"
            : lang === "de"
                ? "Wähle ein Spiel oder Quiz und starte mit dem Lernen!"
                : "Select a game or quiz to start learning!";

    const filterLabelText =
        lang === "sr"
            ? "Filtriraj po tipu kviza:"
            : lang === "de"
                ? "Nach Quiz-Typ filtern:"
                : "Filter by quiz type:";

    const playText =
        lang === "sr" ? "Igraj" : lang === "de" ? "Spielen" : "Play";

    const backButtonText =
        lang === "sr"
            ? "⬅ Nazad na glavni sajt"
            : lang === "de"
                ? "⬅ Zurück zur Hauptseite"
                : "⬅ Back to Main Site";

    return (
        <div className="homepage-container">
            <div className="homepage-inner">
                {/* 🔹 Back button that keeps language when going to Django */}
                <button
                    className="back-btn"
                    onClick={() => {
                        window.location.href = `${ DJANGO_BASE }/?lang=${ lang }`;
                    }}
                >
                    {backButtonText}
                </button>

                <h1>{titleText}</h1>
                <p className="subtitle">{subtitleText}</p>

                <div className="filter-container">
                    <label htmlFor="type-filter">{filterLabelText}</label>
                    <select
                        id="type-filter"
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                    >
                        {quizTypes.map((type) => (
                            <option key={type} value={type}>
                                {formatType(type)}
                            </option>
                        ))}
                    </select>
                </div>

                {loading ? (
                    <p>Loading quizzes...</p>
                ) : (
                    <>
                        <div className="quiz-grid">
                            {paginatedQuizzes.map((quiz) => {
                                const path =
                                    quiz.quiz_type === "drag_drop"
                                        ? `/drag-quiz/${ quiz.id }`
                                        : quiz.quiz_type === "visual"
                                            ? `/visual-quiz/${ quiz.id }`
                                            : quiz.quiz_type === "matching"
                                                ? `/matching-quiz/${ quiz.id }`
                                                : quiz.quiz_type === "pronunciation"
                                                    ? `/pronunciation-quiz/${ quiz.id }`
                                                    : quiz.quiz_type === "audio"
                                                        ? `/audio-quiz/${ quiz.id }`
                                                        : `/quiz/${ quiz.id }`;

                                return (
                                    <div key={quiz.id} className="quiz-card">
                                        {quiz.cover_image && (
                                            <Link to={path}>
                                                <img
                                                    src={quiz.cover_image}
                                                    alt={quiz.title}
                                                    className="quiz-image"
                                                />
                                            </Link>
                                        )}
                                        <h3>{quiz.title}</h3>
                                        <p>Type: {formatType(quiz.quiz_type)}</p>
                                        <p>Difficulty: {quiz.difficulty}</p>
                                        <Link to={path} className="play-btn">
                                            {playText}
                                        </Link>
                                    </div>
                                );
                            })}
                        </div>

                        {quizzes.length > 0 && (
                            <div className="pagination">
                                {currentPage > 1 && (
                                    <button onClick={handlePrev}>← Previous</button>
                                )}
                                <span>
                                    Page {currentPage} of {totalPages}
                                </span>
                                {currentPage < totalPages && (
                                    <button onClick={handleNext}>Next →</button>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default HomePage;
